/**
 * @file Reminder create/edit modal owner for the Shared Calendar domain.
 */

import { appData, state } from "../../app/state/state.js";
import { db } from "../../app/firebase/firebase.js";
import {
    doc,
    getDoc,
} from "https://www.gstatic.com/firebasejs/12.19.0/firebase-firestore.js";
import { closeForm } from "../../shared/forms/modalShell.js";
import { openStandardForm } from "../../shared/forms/standardFormModal.js";
import {
    bindNativeFieldBehavior,
    FIXED_TEXTAREA_CLASSES,
    getNativeFieldWrapperHtml,
    NATIVE_INPUT_CLASSES,
} from "../../shared/forms/nativeFieldStyles.js";
import {
    getRegisteredControlValues,
} from "../../shared/ui/controls/controlCore.js";
import { createDateControl } from "../../shared/ui/controls/dateControl.js";
import { createSelectControl } from "../../shared/ui/controls/selectControl.js";
import { createUserSelectPreset } from "../../shared/ui/controls/controlPresets.js";
import { escapeHTML } from "../../shared/utils/contentUtils.js";
import {
    createCalendarReminder,
    updateCalendarReminder,
} from "./calendarApi.js";
import {
    showCalendarErrorModal,
} from "./calendarModalFeedback.js";
import {
    addOptimisticReminderEntry,
    discardOptimisticEntry,
    reconcileOptimisticReminderEntry,
} from "./calendarOptimisticEntries.js";
import {
    bindCalendarItemThresholdControl,
    CALENDAR_THRESHOLD_ITEM_TYPES,
    getCalendarItemThresholdDraft,
    readCalendarItemThresholds,
    renderCalendarItemThresholdControl,
} from "./notificationThresholdControls.js";

const DEFAULT_REMINDER_TIME = "09:00";
const TIME_INPUT_CLASSES = [
    "calendar-time-input",
    "dfc-date-control-input",
    NATIVE_INPUT_CLASSES,
    "pr-9",
].join(" ");
const TIME_PICKER_BUTTON_CLASSES = [
    "absolute inset-y-0 right-1 my-auto hidden h-7 w-7 sm:inline-flex",
    "items-center justify-center rounded text-gray-500",
    "transition-colors hover:text-gray-700 focus:outline-none",
    "disabled:cursor-not-allowed disabled:text-gray-400",
].join(" ");
const RECURRENCE_FREQUENCIES = Object.freeze([
    { value: "daily", label: "Daily" },
    { value: "weekly", label: "Weekly" },
    { value: "monthly", label: "Monthly" },
    { value: "yearly", label: "Yearly" },
]);

function attrValue(value) {
    return escapeHTML(String(value ?? ""))
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}

function getCurrentUserId() {
    return state.currentUser?.uid || state.currentUserProfile?.id || "";
}

function getUserLabel(user) {
    return String(user?.name || user?.email || user?.id || "");
}

function getCalendarUsersExcluding(userId) {
    const excludedId = String(userId || "");
    return (appData.users || [])
        .filter((user) => (
            user?.id &&
            String(user.id) !== excludedId
        ))
        .map((user) => {
            const userWithoutOrder = { ...user };
            delete userWithoutOrder.order;
            delete userWithoutOrder.sortOrder;
            delete userWithoutOrder.drugSortOrder;
            return userWithoutOrder;
        })
        .sort((userA, userB) => {
            return getUserLabel(userA).localeCompare(getUserLabel(userB));
        });
}

function removeUserId(values, userId) {
    const ids = Array.isArray(values) ? values : [values].filter(Boolean);
    return ids.filter((id) => id && id !== userId);
}

function getTodayDateKey() {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

function getInitialReminderData(reminder) {
    const today = getTodayDateKey();
    const recurrence = reminder?.recurrence || null;
    const recurrenceActive = Boolean(
        recurrence?.frequency &&
        recurrence.frequency !== "none" &&
        !recurrence.stoppedAt,
    );
    const data = {
        id: reminder?.id || reminder?.sourceId || "",
        title: reminder?.title || "",
        reminderDate: reminder?.reminderDate || reminder?.startDate || today,
        reminderTime: reminder?.reminderTime || reminder?.startTime || DEFAULT_REMINDER_TIME,
        description: reminder?.description || "",
        creatorId: reminder?.creatorId || getCurrentUserId(),
        assignedUserIds: removeUserId(
            Array.isArray(reminder?.assignedUserIds) ?
                reminder.assignedUserIds :
                [],
            reminder?.creatorId || getCurrentUserId(),
        ),
        updatedAt: reminder?.updatedAt || null,
        recurrenceEnabled: recurrenceActive,
        recurrenceFrequency: recurrenceActive ? recurrence.frequency : "weekly",
        recurrenceInterval: Number(recurrence?.interval || 1),
        recurrenceEndDate: recurrence?.endDate || "",
    };
    data.notificationThreshold = getCalendarItemThresholdDraft({
        itemType: CALENDAR_THRESHOLD_ITEM_TYPES.REMINDER,
        item: reminder,
        config: appData.config || {},
    });
    return data;
}

async function resolveReminderFormSource(reminder) {
    const sourceId = reminder?.sourceType === "reminder" ?
        reminder?.sourceId || reminder?.id || "" :
        "";
    if (!sourceId || Array.isArray(reminder?.notificationThresholds)) {
        return reminder;
    }

    try {
        const snapshot = await getDoc(doc(db, "calendar_reminders", sourceId));
        if (!snapshot.exists()) {return reminder;}
        return { id: snapshot.id, ...snapshot.data() };
    } catch (error) {
        console.error("Error loading Calendar reminder for edit:", error);
        return reminder;
    }
}

function getTextInputHtml({
    name,
    label,
    value = "",
    type = "text",
    required = false,
}) {
    return `
        <input
            type="${attrValue(type)}"
            name="${attrValue(name)}"
            aria-label="${attrValue(label || name)}"
            value="${attrValue(value)}"
            class="${NATIVE_INPUT_CLASSES}"
            ${required ? "required" : ""}
            data-calendar-reminder-input
        >
    `;
}

function getTimeInputHtml({
    label,
    value = "",
    required = false,
}) {
    return `
        <div class="relative" data-calendar-time-control>
            <input
                type="time"
                name="reminderTimeInput"
                aria-label="${attrValue(label || "Time")}"
                value="${attrValue(value)}"
                class="${TIME_INPUT_CLASSES}"
                ${required ? "required" : ""}
                data-calendar-empty="${value ? "false" : "true"}"
                data-calendar-reminder-input
            >
            <button
                type="button"
                class="${TIME_PICKER_BUTTON_CLASSES}"
                aria-label="Open time picker"
                tabindex="-1"
                data-calendar-time-picker-button
            >
                <span class="material-symbols-outlined text-[20px] leading-none" aria-hidden="true">expand_more</span>
            </button>
        </div>
    `;
}

function getRecurrenceFieldsHtml(reminder) {
    return `
        <div
            class="rounded-lg border border-gray-200 p-4 md:col-span-2"
            data-calendar-reminder-recurrence
            data-recurrence-frequency="${attrValue(reminder.recurrenceFrequency || "weekly")}"
            data-recurrence-end-date="${attrValue(reminder.recurrenceEndDate || "")}"
        >
            <div class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div class="min-w-0">
                    <div class="font-medium text-gray-900">Repeat reminder</div>
                    <p class="mt-1 text-sm text-gray-500">Create a recurring reminder from this date and time.</p>
                </div>
                <label class="toggle-switch shrink-0">
                    <input
                        type="checkbox"
                        name="recurrenceEnabled"
                        ${reminder.recurrenceEnabled ? "checked" : ""}
                        data-calendar-reminder-recurrence-enabled
                    >
                    <span class="slider"></span>
                </label>
            </div>
            <div class="mt-4 grid gap-3 sm:grid-cols-[minmax(0,10rem)_minmax(0,8rem)_minmax(0,12rem)]" data-calendar-reminder-recurrence-fields>
                <div class="block">
                    <span class="block text-sm font-medium text-gray-700">Frequency</span>
                    <div
                        class="mt-1"
                        data-calendar-reminder-recurrence-frequency-control
                    ></div>
                </div>
                <label class="block">
                    <span class="block text-sm font-medium text-gray-700">Every</span>
                    <input
                        type="number"
                        name="recurrenceInterval"
                        min="1"
                        max="365"
                        step="1"
                        inputmode="numeric"
                        class="${NATIVE_INPUT_CLASSES} mt-1 w-full"
                        value="${attrValue(reminder.recurrenceInterval || 1)}"
                        data-calendar-reminder-recurrence-field
                    >
                </label>
                <div class="block">
                    <span class="block text-sm font-medium text-gray-700">Ends</span>
                    <div
                        class="mt-1"
                        data-calendar-reminder-recurrence-end-control
                    ></div>
                </div>
            </div>
        </div>
    `;
}

function getReminderFieldsHtml(rawReminder) {
    const reminder = getInitialReminderData(rawReminder);
    return `
        <div class="grid grid-cols-1 gap-4 md:grid-cols-2" data-calendar-reminder-fields>
            ${getNativeFieldWrapperHtml("Title", getTextInputHtml({
                name: "title",
                label: "Title",
                value: reminder.title,
                required: true,
            }), { className: "md:col-span-2", required: true })}
            <div class="grid gap-4 md:col-span-2 md:grid-cols-2">
                <div data-calendar-reminder-date-control></div>
                ${getNativeFieldWrapperHtml("Time", getTimeInputHtml({
                    label: "Time",
                    value: reminder.reminderTime || DEFAULT_REMINDER_TIME,
                    required: true,
                }), { required: true })}
            </div>
            <input
                type="hidden"
                name="reminderTime"
                value="${attrValue(reminder.reminderTime || DEFAULT_REMINDER_TIME)}"
                data-calendar-reminder-time-hidden
            >
            ${getNativeFieldWrapperHtml("Owner", `
                <div data-calendar-reminder-owner-control></div>
            `, { required: true })}
            ${getNativeFieldWrapperHtml("Assigned Users", `
                <div data-calendar-reminder-assigned-control></div>
            `)}
            ${getNativeFieldWrapperHtml("Description", `
                <textarea
                    name="description"
                    rows="4"
                    class="${FIXED_TEXTAREA_CLASSES}"
                    data-calendar-reminder-input
                >${escapeHTML(reminder.description || "")}</textarea>
            `, { className: "md:col-span-2" })}
            <div class="md:col-span-2">
                ${renderCalendarItemThresholdControl({
                    itemType: CALENDAR_THRESHOLD_ITEM_TYPES.REMINDER,
                    threshold: reminder.notificationThreshold,
                    label: "Reminder Notifications",
                })}
            </div>
            ${getRecurrenceFieldsHtml(reminder)}
        </div>
    `;
}

function collectReminderData(itemForm, controlValues = {}) {
    const formData = new FormData(itemForm);
    const reminderTime = String(formData.get("reminderTime") || DEFAULT_REMINDER_TIME);
    const notificationThresholds = readCalendarItemThresholds(itemForm, {
        itemType: CALENDAR_THRESHOLD_ITEM_TYPES.REMINDER,
    });
    return {
        title: String(formData.get("title") || "").trim(),
        reminderDate: String(controlValues.reminderDate || ""),
        reminderTime,
        notificationThresholds,
        recurrence: collectReminderRecurrenceData(
            formData,
            controlValues,
            String(controlValues.reminderDate || ""),
            reminderTime,
        ),
        description: String(formData.get("description") || "").trim() || null,
        creatorId: String(controlValues.creatorId || getCurrentUserId()),
        assignedUserIds: removeUserId(
            Array.isArray(controlValues.assignedUserIds) ?
                controlValues.assignedUserIds :
                [],
            String(controlValues.creatorId || getCurrentUserId()),
        ),
    };
}

function collectReminderRecurrenceData(
    formData,
    controlValues,
    reminderDate,
    reminderTime,
) {
    if (formData.get("recurrenceEnabled") !== "on") {return null;}
    return {
        frequency: String(controlValues.recurrenceFrequency || "weekly"),
        interval: Number(formData.get("recurrenceInterval") || 1),
        endDate: String(controlValues.recurrenceEndDate || "") || null,
        anchorDate: reminderDate,
        anchorTime: reminderTime,
    };
}

function setSubmitting(itemForm, isSubmitting) {
    const submitButton = itemForm.querySelector("button[type=\"submit\"]");
    if (!submitButton) {return;}
    submitButton.disabled = isSubmitting;
    submitButton.classList.toggle("opacity-60", isSubmitting);
    submitButton.textContent = isSubmitting ? "Saving..." : "Save";
}

function mountReminderControls(itemForm, reminderData) {
    const dateRoot = itemForm.querySelector("[data-calendar-reminder-date-control]");
    const ownerRoot = itemForm.querySelector("[data-calendar-reminder-owner-control]");
    const assignedRoot = itemForm.querySelector("[data-calendar-reminder-assigned-control]");
    let assignedControl = null;

    const mountAssignedControl = (ownerId, value = []) => {
        if (!assignedRoot) {return;}
        assignedControl?.destroy();
        assignedControl = createUserSelectPreset({
            root: assignedRoot,
            appData,
            users: getCalendarUsersExcluding(ownerId),
            collectionName: "calendar",
            name: "assignedUserIds",
            value: removeUserId(value, ownerId),
            multiple: true,
            searchable: true,
            portalMenu: true,
            placeholder: "+ Add users",
            accessibleName: "Assigned Users",
        });
    };

    const syncAssignedOwnerExclusion = (ownerId) => {
        mountAssignedControl(
            ownerId,
            Array.isArray(assignedControl?.value) ? assignedControl.value : [],
        );
    };

    if (dateRoot) {
        createDateControl({
            root: dateRoot,
            name: "reminderDate",
            nativeInputName: "reminderDate",
            type: "date",
            label: "Date",
            value: reminderData.reminderDate,
            required: true,
            requiredMessage: "Date is required.",
        });
    }

    if (ownerRoot) {
        createUserSelectPreset({
            root: ownerRoot,
            appData,
            collectionName: "calendar",
            name: "creatorId",
            value: reminderData.creatorId,
            multiple: false,
            placeholder: "+ Add owner",
            accessibleName: "Owner",
            required: true,
            requiredMessage: "Owner is required.",
            clearable: false,
            portalMenu: true,
            menuBoundary: "#form-modal",
            onChange: syncAssignedOwnerExclusion,
        });
    }

    if (assignedRoot) {
        mountAssignedControl(reminderData.creatorId, reminderData.assignedUserIds);
    }
}

function bindReminderTimeCompatibility(itemForm) {
    const visibleTime = itemForm.querySelector("input[name=\"reminderTimeInput\"]");
    const hiddenTime = itemForm.querySelector("[data-calendar-reminder-time-hidden]");
    const sync = () => {
        if (hiddenTime) {
            hiddenTime.value = visibleTime?.value || DEFAULT_REMINDER_TIME;
        }
        if (visibleTime) {
            visibleTime.dataset.calendarEmpty = visibleTime.value ? "false" : "true";
        }
    };
    const openPicker = () => {
        if (!visibleTime || visibleTime.disabled) {return;}
        visibleTime.focus({ preventScroll: true });
        if (typeof visibleTime.showPicker === "function") {
            try {
                visibleTime.showPicker();
            } catch {
                // Focus-only fallback for browsers that reject showPicker.
            }
        }
    };
    visibleTime?.addEventListener("input", sync);
    visibleTime?.addEventListener("change", sync);
    visibleTime
        ?.closest("[data-calendar-time-control]")
        ?.querySelector("[data-calendar-time-picker-button]")
        ?.addEventListener("click", openPicker);
    sync();
}

function bindReminderRecurrenceControls(itemForm) {
    const root = itemForm.querySelector("[data-calendar-reminder-recurrence]");
    const toggle = root?.querySelector("[data-calendar-reminder-recurrence-enabled]");
    const fieldsRoot = root?.querySelector("[data-calendar-reminder-recurrence-fields]");
    const frequencyRoot = root?.querySelector(
        "[data-calendar-reminder-recurrence-frequency-control]",
    );
    const endDateRoot = root?.querySelector(
        "[data-calendar-reminder-recurrence-end-control]",
    );
    const fields = Array.from(root?.querySelectorAll(
        "[data-calendar-reminder-recurrence-field]",
    ) || []);
    const frequencyControl = frequencyRoot ? createSelectControl({
        root: frequencyRoot,
        name: "recurrenceFrequency",
        value: itemForm.querySelector("[data-calendar-reminder-recurrence]")
            ?.dataset.recurrenceFrequency || "weekly",
        options: RECURRENCE_FREQUENCIES,
        accessibleName: "Recurrence frequency",
        placeholder: "Select frequency",
        required: true,
        requiredMessage: "Frequency is required.",
        portalMenu: true,
        menuBoundary: "#form-modal",
    }) : null;
    const endDateControl = endDateRoot ? createDateControl({
        root: endDateRoot,
        name: "recurrenceEndDate",
        nativeInputName: "recurrenceEndDate",
        type: "date",
        value: itemForm.querySelector("[data-calendar-reminder-recurrence]")
            ?.dataset.recurrenceEndDate || "",
    }) : null;
    const sync = () => {
        const enabled = toggle?.checked === true;
        fieldsRoot?.classList.toggle("hidden", !enabled);
        fields.forEach((field) => {
            field.disabled = !enabled;
            field.required = enabled;
        });
        frequencyControl?.update({ disabled: !enabled, required: enabled });
        endDateControl?.update({ disabled: !enabled });
    };
    toggle?.addEventListener("change", sync);
    sync();
}

/**
 * Opens the reminder create/edit modal.
 * @param {object|null} [reminder=null] Reminder data or date seed.
 * @returns {void}
 */
export async function openReminderFormModal(reminder = null) {
    const sourceReminder = await resolveReminderFormSource(reminder);
    const reminderData = getInitialReminderData(sourceReminder);

    openStandardForm("calendar_reminder", reminderData.id ? reminderData : null, {
        getFieldsHtml: () => getReminderFieldsHtml(sourceReminder),
        afterRender({ itemForm }) {
            const formTitle = document.querySelector("#form-title");
            if (formTitle) {
                formTitle.textContent = reminderData.id ?
                    "Edit Reminder" :
                    "Add Reminder";
            }
            mountReminderControls(itemForm, reminderData);
            bindCalendarItemThresholdControl(itemForm, {
                itemType: CALENDAR_THRESHOLD_ITEM_TYPES.REMINDER,
            });
            bindReminderTimeCompatibility(itemForm);
            bindReminderRecurrenceControls(itemForm);
            bindNativeFieldBehavior(itemForm);
            itemForm.onsubmit = async (event) => {
                event.preventDefault();
                const controlExtraction = getRegisteredControlValues(itemForm, {
                    validate: true,
                });
                if (!controlExtraction.valid || !itemForm.reportValidity()) {
                    return;
                }

                const payload = collectReminderData(
                    itemForm,
                    controlExtraction.values,
                );
                const optimisticId = addOptimisticReminderEntry(
                    payload,
                    reminderData.id || null,
                );
                closeForm();
                try {
                    setSubmitting(itemForm, true);
                    const response = reminderData.id ?
                        await updateCalendarReminder(
                            reminderData.id,
                            payload,
                            reminderData.updatedAt,
                        ) :
                        await createCalendarReminder(payload);
                    reconcileOptimisticReminderEntry(optimisticId, response);
                } catch (error) {
                    console.error("Error saving Calendar reminder:", error);
                    discardOptimisticEntry(optimisticId);
                    showCalendarErrorModal(
                        "Reminder Save Failed",
                        error,
                        "Reminder could not be saved.",
                    );
                } finally {
                    setSubmitting(itemForm, false);
                }
            };
        },
    });
}
